import React, { useState, useEffect, useRef } from 'react';
import { ThumbsDown, ThumbsUp } from "@/lib/icons";

const SwipeableTextCards = ({ 
  items, 
  onSwipe, 
  onFinish, 
  isSubmitting, 
  swipedFactors, 
  assessmentType,
  totalOriginalItems: propsTotalOriginalItems
}) => {
  // const [currentIndex, setCurrentIndex] = useState(0); // REMOVED
  const [direction, setDirection] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false); // Track transition state
  const [swipingOutCardId, setSwipingOutCardId] = useState(null); // ADDED: Track ID of card being swiped out
  const cardRef = useRef(null);
  
  // Track total number of original items for progress calculation
  const totalToDisplay = propsTotalOriginalItems !== undefined ? propsTotalOriginalItems : (items.length + (swipedFactors ? swipedFactors.size : 0));
  
  // Track touch/mouse positions
  const [touchStart, setTouchStart] = useState({ x: 0, y: 0 });
  const [touchPosition, setTouchPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  
  // Calculate completed cards and progress percentage
  const completedCards = swipedFactors ? swipedFactors.size : 0; // Directly use the size of the passed swipedFactors Set
  const completionPercentage = totalToDisplay > 0 ? (completedCards / totalToDisplay) * 100 : 0;
  
  // Handle start of touch/mouse event
  const handleTouchStart = (e) => {
    if (isSubmitting || isTransitioning) return;
    const posX = e.type.includes('mouse') ? e.clientX : e.touches[0].clientX;
    const posY = e.type.includes('mouse') ? e.clientY : e.touches[0].clientY;
    setTouchStart({ x: posX, y: posY });
    setTouchPosition({ x: posX, y: posY });
    setIsDragging(true);
  };
  
  // Handle touch/mouse movement
  const handleTouchMove = (e) => {
    if (!isDragging || isSubmitting || isTransitioning) return;
    const posX = e.type.includes('mouse') ? e.clientX : e.touches[0].clientX;
    const posY = e.type.includes('mouse') ? e.clientY : e.touches[0].clientY;
    setTouchPosition({ x: posX, y: posY });
  };
  
  // Handle end of touch/mouse event
  const handleTouchEnd = () => {
    if (!isDragging || isSubmitting || isTransitioning) return;
    const deltaX = touchPosition.x - touchStart.x;
    const threshold = cardRef.current ? cardRef.current.offsetWidth * 0.4 : 100; // 40% of card width
    
    if (deltaX > threshold) {
      // Swiped right (Strength)
      handleSwipe('right');
    } else if (deltaX < -threshold) {
      // Swiped left (Opportunity)
      handleSwipe('left');
    } else {
      // Reset position if not swiped far enough
      setDirection(null);
    }
    
    setIsDragging(false);
  };
  
  // Handle successful swipe
  const handleSwipe = (dir) => {
    if (isSubmitting || isTransitioning || !items || items.length === 0) return;
    
    const itemToSwipe = items[0]; // Get the card to be swiped
    if (!itemToSwipe) {
      console.error('No item found at items[0] to swipe');
      setIsTransitioning(false); // Still reset transitioning if no item
      return;
    }

    setIsTransitioning(true);
    setDirection(dir); 
    setSwipingOutCardId(itemToSwipe.id); // ADDED: Set ID of card being swiped
    
    try {
      onSwipe(null, null, itemToSwipe.id, dir === 'right' ? 'like' : 'dislike');

      setTimeout(() => {
        setDirection(null); 
        setSwipingOutCardId(null); // ADDED: Reset ID
        setIsTransitioning(false); 
      }, 400);
    } catch (error) {
      console.error('Error during card swipe:', error);
      setDirection(null);
      setSwipingOutCardId(null); // ADDED: Reset ID on error
      setIsTransitioning(false);
    }
  };
  
  // Calculate card transform based on touch position
  const getCardStyle = (item, index) => {
    // Style for STACKED cards (not the top one)
    if (index !== 0) {
      return {
        transform: `scale(${1 - (index * 0.05)}) translateY(${index * 10}px)`,
        opacity: index < 3 ? (1 - (index * 0.25)) : 0, // Fade out cards further down
        transition: 'transform 0.4s ease, opacity 0.4s ease',
        pointerEvents: 'none', // Only top card interactive
        zIndex: 10 - index,
      };
    }

    // Styles for the TOP card (index === 0)
    if (isDragging) {
      const deltaX = touchPosition.x - touchStart.x;
      const rotate = deltaX * 0.1;
      return {
        transform: `translateX(${deltaX}px) rotate(${rotate}deg)`,
        transition: 'none', // No transition while dragging
        zIndex: 20, // Ensure dragging card is on top
      };
    }
    
    // Style for the card that IS CURRENTLY SWIPING OUT
    if (direction && item && item.id === swipingOutCardId) {
      const translateX = direction === 'right' ? '150%' : '-150%'; // Use percentage for responsiveness
      const rotateDeg = direction === 'right' ? 30 : -30;
      return {
        transform: `translateX(${translateX}) rotate(${rotateDeg}deg)`,
        transition: 'transform 0.4s cubic-bezier(0.68, -0.55, 0.27, 1.55)', // Smoother swipe animation
        zIndex: 20, // Keep it on top during animation
      };
    }

    // Default style for the TOP card when IDLE (not dragging, not swiping out)
    return {
      transform: 'scale(1) translateY(0)',
      opacity: 1,
      transition: 'transform 0.4s ease, opacity 0.4s ease',
      pointerEvents: 'auto',
      zIndex: 15, // Above stacked cards but below a card being actively swiped/dragged
    };
  };
  
  // Card background color based on swipe direction
  const getCardBackground = () => {
    if (!isDragging) return '';
    const deltaX = touchPosition.x - touchStart.x;
    if (deltaX > 50) {
      // Swiping right - positive indication (Strength)
      return 'rgba(82, 196, 26, 0.1)';
    } else if (deltaX < -50) {
      // Swiping left - negative indication (Opportunity)
      return 'rgba(255, 77, 79, 0.1)';
    }
    return '';
  };
  
  // Add and remove event listeners
  useEffect(() => {
    document.addEventListener('mousemove', handleTouchMove);
    document.addEventListener('mouseup', handleTouchEnd);
    return () => {
      document.removeEventListener('mousemove', handleTouchMove);
      document.removeEventListener('mouseup', handleTouchEnd);
    };
  }, [isDragging, touchStart, touchPosition, isSubmitting, isTransitioning]);
  
  if (items.length === 0) return null;
  
  // Fixed calculation for factor number display
  const calculateFactorNumber = () => {
    return {
      // current: currentCardNumber, // currentCardNumber is already calculated above
      current: (swipedFactors ? swipedFactors.size : 0) + 1, // More direct calculation for current card number
      total: totalToDisplay // Total original items
    };
  };
  
  // Show more cards to improve preloading and visual cues
  // const visibleCards = items.slice(currentIndex, currentIndex + 5); // MODIFIED
  const visibleCards = items.slice(0, Math.min(items.length, 5)); // Always slice from the beginning
  
  return (
    <div className="swipeable-cards-container flex flex-col items-center justify-center">
      {/* Progress indicator showing current card / total */}
      <div className="progress-indicator mb-4 w-full max-w-xs">
        <div className="flex justify-between items-center mb-1">
          <span className="text-sm font-medium">Progress</span>
          <span className="text-sm font-medium">{completedCards} of {totalToDisplay} completed</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-score-5 h-2 rounded-full transition-all duration-300" 
            style={{ width: `${completionPercentage}%` }} 
          />
        </div>
      </div>
      
      {/* Card stack container with improved positioning */}
      <div className="relative w-full max-w-xs h-[450px]">
        {visibleCards.map((item, index) => {
          const factorNumbers = calculateFactorNumber();
          const isTopCard = index === 0;
          
          return (
            <div
              key={item.id} 
              ref={isTopCard ? cardRef : null}
              className={`swipeable-card absolute top-0 left-0 right-0 ${isTopCard && !isTransitioning ? 'active' : ''}`}
              style={{
                ...getCardStyle(item, index),
                backgroundColor: isTopCard && isDragging ? getCardBackground() : '',
              }}
              onMouseDown={isTopCard && !isSubmitting && !isTransitioning ? handleTouchStart : null}
              onTouchStart={isTopCard && !isSubmitting && !isTransitioning ? handleTouchStart : null}
              onTouchMove={isTopCard && !isSubmitting && !isTransitioning ? handleTouchMove : null}
              onTouchEnd={isTopCard && !isSubmitting && !isTransitioning ? handleTouchEnd : null}
            >
              {isTopCard && (
                <>
                  <div 
                    className="flex flex-row items-center justify-center gap-1.5 absolute top-0 left-0 right-0 whitespace-nowrap" 
                    style={isDragging && touchPosition.x - touchStart.x < -50 ?
                      {
                        display: 'flex',
                        opacity: Math.min(Math.abs(touchPosition.x - touchStart.x) / 200, 1),
                        transition: 'opacity 0.4s ease',
                        backgroundColor: 'rgba(255, 77, 79, 0.1)',
                        padding: '6px 16px',
                        zIndex: 30,
                        color: 'var(--color-score-1)',
                        fontWeight: '600',
                        fontSize: '12px',
                        height: '28px'
                      } : 
                      { 
                        display: 'none',
                        transition: 'opacity 0.4s ease'
                      }
                    }
                  >
                    <ThumbsDown className="h-4 w-4" />
                    Opportunity
                  </div>
                  <div 
                    className="flex flex-row items-center justify-center gap-1.5 absolute top-0 left-0 right-0 whitespace-nowrap" 
                    style={isDragging && touchPosition.x - touchStart.x > 50 ?
                      {
                        display: 'flex',
                        opacity: Math.min(Math.abs(touchPosition.x - touchStart.x) / 200, 1),
                        transition: 'opacity 0.4s ease',
                        backgroundColor: 'rgba(0, 104, 114, 0.1)',
                        padding: '6px 16px',
                        zIndex: 30,
                        color: 'var(--color-score-5)',
                        fontWeight: '600',
                        fontSize: '12px',
                        height: '28px'
                      } : 
                      { 
                        display: 'none',
                        transition: 'opacity 0.4s ease'
                      }
                    }
                  >
                    <ThumbsUp className="h-4 w-4" />
                    Strength
                  </div>
                </>
              )}
              
              <div className="swipeable-card-content">
                <p className="text-sm font-medium text-gray-500 mb-1 mt-3">{item.category}</p>
                <p className="text-xs font-semibold text-primary uppercase tracking-wide mb-3">
                  {item.contributor_name}
                </p>
                <div className={`swipeable-card-text ${item.text.length > 200 ? 'small-text' : ''}`}>
                  {item.text}
                </div>
              </div>
              
              {isTopCard && (
                <div className="flex w-full absolute bottom-0 left-0 right-0 rounded-b-lg overflow-hidden shadow-inner">
                  <button 
                    className="flex-1 py-4 bg-score-1 hover:bg-score-1/90 text-score-1-foreground font-medium flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => handleSwipe('left')}
                    disabled={isSubmitting || isTransitioning}
                  >
                    <ThumbsDown className="h-5 w-5 mr-2" />
                    Opportunity
                  </button>
                  <button 
                    className="flex-1 py-4 bg-score-5 hover:bg-score-5/90 text-score-5-foreground font-medium flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => handleSwipe('right')}
                    disabled={isSubmitting || isTransitioning}
                  >
                    <ThumbsUp className="h-5 w-5 mr-2" />
                    Strength
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SwipeableTextCards; 